import React, { forwardRef } from "react";
import { motion, useReducedMotion } from "motion/react";
import { Soft, type SoftProps } from "./Soft";
import { entryVariants, resolveTransition } from "@/components/Chat/motion";

export type AnimatedCardProps = SoftProps;

/**
 * A neumorphic `Soft` surface that fades + rises in on mount. The single
 * pattern for inline content cards rendered in the chat stream (artifacts,
 * media, subagent panels) so they all share the same extruded look and the
 * same subtle entry motion. Honors `prefers-reduced-motion` (no travel, no
 * fade) via `resolveTransition`.
 *
 * Implemented as a `motion.div` wrapping `Soft` — the same pattern the message
 * bubbles use — rather than `motion.create(Soft)`, which collides with the
 * Box `onDrag` typing.
 */
export const AnimatedCard = forwardRef<HTMLDivElement, AnimatedCardProps>(
  function AnimatedCard({ children, ...softProps }, ref) {
    const reduce = useReducedMotion() ?? false;
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={entryVariants}
        transition={resolveTransition(reduce)}
      >
        <Soft ref={ref} {...softProps}>
          {children}
        </Soft>
      </motion.div>
    );
  },
);
